import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import { courseAPI } from '../services/api';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import '../styles/components/_chapter-summary.scss';
import { getLocalizedText, getDisplayLocale } from '../utils/i18n';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { normalizeId } from '../utils/id';
import { useDiagnosticAccessGuard } from '../hooks/useDiagnosticAccessGuard';
import { LockIcon } from '../utils/icons';

const ChapterSummary: React.FC = () => {
  const { courseId, chapterId } = useParams<{ 
    courseId: string; 
    chapterId: string;
  }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const [course, setCourse] = useState<any>(null);
  const [currentChapter, setCurrentChapter] = useState<any>(null);
  const [nextChapter, setNextChapter] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useDiagnosticAccessGuard();

  const isChapterLocked = (chapter: any): boolean => {
    if (chapter?.isLocked === true) return true;
    const rawUnlockDate = chapter?.unlockDate;
    if (!rawUnlockDate) return false;
    const unlockDate = new Date(rawUnlockDate);
    if (Number.isNaN(unlockDate.getTime())) return false;
    return unlockDate.getTime() > Date.now();
  };

  const findNextChapter = (chapters: any[], currentOrder: number, preferredId?: string | null): any | null => {
    const sorted = [...(chapters || [])].sort(
      (a: any, b: any) => Number(a.order || 0) - Number(b.order || 0)
    );
    if (preferredId) {
      const preferred = sorted.find((ch: any) => normalizeId(ch._id) === normalizeId(preferredId));
      if (preferred && Number(preferred.order || 0) > currentOrder) {
        return preferred;
      }
    }
    return sorted.find((ch: any) => Number(ch.order || 0) > currentOrder) || null;
  };

  const formatUnlockDate = (unlockDate: string): string => {
    const date = new Date(unlockDate);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleString(locale, { dateStyle: 'long', timeStyle: 'short' });
  };

  useEffect(() => {
    if (courseId && chapterId) {
      loadData();
    }
  }, [courseId, chapterId]);

  const loadData = async () => {
    if (!courseId || !chapterId) {
      setError(t('common.missingParameters'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setNextChapter(null);
      
      // Load course data
      const courseData = await courseAPI.getCourse(courseId);
      setCourse(courseData);

      // Find current chapter
      const currentChapterData = courseData.chapters?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId));
      if (currentChapterData) {
        setCurrentChapter(currentChapterData);
      } else {
        setError(t('chapterSummary.chapterNotFound'));
        setLoading(false);
        return;
      }

      // Chapitre suivant (y compris s'il est verrouillé — pour afficher son résumé).
      const currentOrder = Number(currentChapterData.order || 0);
      const nextChapterId = searchParams.get('next');
      const nextChapterData = findNextChapter(
        courseData.chapters || [],
        currentOrder,
        nextChapterId
      );
      if (nextChapterData) {
        setNextChapter(nextChapterData);
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(getLearnerFacingApiError(err, t));
    } finally {
      setLoading(false);
    }
  };


  const handleContinue = () => {
    if (nextChapter && isNextChapterLocked) {
      return;
    }
    if (nextChapter) {
      // Navigate to first item of next chapter
      const firstItem = nextChapter.items?.sort((a: any, b: any) => a.order - b.order)[0];
      if (firstItem) {
        const firstItemId = normalizeId(firstItem?._id || firstItem?.id);
        const nextChapterId = normalizeId(nextChapter._id);
        if (!firstItemId || !nextChapterId) return;
        navigate(`/course/${courseId}/chapter/${nextChapterId}/${firstItem.itemType}/${firstItemId}`);
      } else {
        const nextChapterId = normalizeId(nextChapter._id);
        if (!nextChapterId) return;
        const nextChapterRequiresRating = nextChapter.requiresRating !== false;
        navigate(`/course/${courseId}/chapter/${nextChapterId}/${nextChapterRequiresRating ? 'rating' : 'summary'}`);
      }
    } else if (isFinalAssessmentReady) {
      // Pas de chapitre suivant mais la mesure d'impact finale est disponible.
      navigate(`/course/${courseId}/assessment/final`);
    } else {
      // Sinon retour au tableau de bord de la formation.
      navigate(`/course/${courseId}`);
    }
  };

  /** True si la mesure d'impact finale est dispo et pas encore soumise. */
  const isFinalAssessmentReady: boolean = (() => {
    const a = course?.assessment;
    if (!a?.enabled) return false;
    if (a.final?.submitted) return false;
    return !!a.eligibility?.finalImpactAvailable;
  })();

  // Show loading state
  if (loading) {
    return (
      <Box className="chapter-summary__loading">
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error && !currentChapter) {
    return (
      <Box className="chapter-summary__error">
        <Typography variant="h6" className="chapter-summary__error-text">
          {error}
        </Typography>
      </Box>
    );
  }

  const isNextChapterLocked = nextChapter ? isChapterLocked(nextChapter) : false;

  return (
    <Box className="chapter-summary">
      {/* Main Content Card */}
      <Box className="chapter-summary__card">
        {/* Header with Flag Graphic */}
        <div className="chapter-summary__header-section">
          <div className="chapter-summary__graphic">
            <img
              src={`${process.env.PUBLIC_URL || ''}/chapter-completion-graphic.png`}
              alt={t('chapterSummary.chapterCompleted')}
              className="chapter-summary__completion-image"
              onError={(e) => {
                console.error('Chapter completion graphic not found');
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div className="chapter-summary__title-section">
            <p className="chapter-summary__title">
              {t('chapterSummary.title')}
            </p>
            <p className="chapter-summary__subtitle">
              {t('chapterSummary.subtitle')}
            </p>
          </div>
        </div>

        {/* Current Chapter Items */}
        {currentChapter && (
          <Box className="chapter-summary__current-section">
            <Box className="chapter-summary__section-header">
              <p className="chapter-summary__section-title">{t('chapterSummary.currentSection')}</p>
            </Box>
            <Box className="chapter-summary__items-list">
              <Box className="chapter-summary__item-text">
                <MarkdownRenderer content={getLocalizedText(currentChapter.summary) || ''} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Next Chapter Preview (y compris si verrouillé) */}
        {nextChapter && (
          <Box className={`chapter-summary__next-section${isNextChapterLocked ? ' chapter-summary__next-section--locked' : ''}`}>
            <Box className="chapter-summary__section-header">
              <p className="chapter-summary__section-title">{t('chapterSummary.nextSection')}</p>
              {isNextChapterLocked && (
                <LockIcon size={18} className="chapter-summary__lock-icon" />
              )}
            </Box>
            <Box className="chapter-summary__items-list">
              <Box className="chapter-summary__item-text">
                <MarkdownRenderer content={getLocalizedText(nextChapter.summary) || ''} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Continue Button */}
        {(nextChapter || isFinalAssessmentReady) && (
          <Box className="chapter-summary__actions">
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={!!nextChapter && isNextChapterLocked}
              startIcon={nextChapter && isNextChapterLocked ? <LockIcon size={18} /> : undefined}
              className={`chapter-summary__button${isNextChapterLocked ? ' chapter-summary__button--locked' : ' button-primary'}`}
            >
              {nextChapter
                ? isNextChapterLocked
                  ? t('chapterSummary.continueButtonLocked')
                  : t('chapterSummary.continueButton')
                : t('chapterSummary.startFinalAssessmentButton')}
            </Button>
            {nextChapter && isNextChapterLocked && nextChapter.unlockDate && (
              <Typography variant="body2" className="chapter-summary__unlock-hint">
                {t('chapterSummary.nextChapterUnlocksOn', {
                  date: formatUnlockDate(nextChapter.unlockDate),
                })}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChapterSummary;
